import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  IconButton,
  Flex,
  Text,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { ArrowDownUp, Check, GripHorizontal, X, Pin } from "lucide-react";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DoubleArrowIcon from "@material-ui/icons/DoubleArrow";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
  DragStartEvent,
  DragCancelEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";

import SortableRow from "./SortableRow";
import TableCell from "./TableCell";
import RowActions from "./RowActions";
import { ITableItem, TableColumn } from "./types";

interface TableProps<T extends ITableItem> {
  data: T[];
  columns: TableColumn<T>[];
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  onReorder?: (newData: T[]) => void;
  onSaveOrder?: (newData: T[]) => void;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  canDeleteRow?: (item: T) => boolean;
  tableStyle?: React.CSSProperties;
  rowStyle?: React.CSSProperties;
  dragStyle?: React.CSSProperties;
  disableDragAndDrop?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  isLoading?: boolean;
}

const DraggableTable = <T extends ITableItem>({
  data,
  columns,
  onEdit,
  onDelete,
  onReorder,
  onSaveOrder,
  showEditButton = true,
  showDeleteButton = true,
  canDeleteRow,
  tableStyle,
  rowStyle,
  dragStyle,
  disableDragAndDrop = false,
  enablePagination = false,
  pageSize = 10,
  currentPage = 0,
  totalItems = 0,
  onPageChange,
  isLoading = false,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const [isDraggable, setIsDraggable] = useState(false);
  const [currentData, setCurrentData] = useState<T[]>(data);
  const [originalData, setOriginalData] = useState<T[]>(data);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  const canDrag = isDraggable && !disableDragAndDrop;
  const sortableItems = useMemo(
    () => currentData.map((i) => i.id!),
    [currentData]
  );

  const pinColumn = useMemo(
    () => columns.find((col) => col.showPin && col.onPinToggle),
    [columns]
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (tableContainerRef.current && !disableDragAndDrop) {
      tableContainerRef.current.style.overflow = "hidden";
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (tableContainerRef.current && !disableDragAndDrop) {
      tableContainerRef.current.style.overflow = "auto";
    }

    if (disableDragAndDrop) return;
    if (!over || active.id === over.id) return;

    const oldIndex = currentData.findIndex((i) => i.id === active.id);
    const newIndex = currentData.findIndex((i) => i.id === over.id);
    const newData = arrayMove(currentData, oldIndex, newIndex);

    setCurrentData(newData);
  };

  const handleDragCancel = (event: DragCancelEvent) => {
    if (tableContainerRef.current && !disableDragAndDrop) {
      tableContainerRef.current.style.overflow = "auto";
    }
  };

  const toggleDragMode = () => {
    if (!disableDragAndDrop) {
      if (isDraggable) {
        onSaveOrder?.(currentData);
        setOriginalData(currentData);
      }
      setIsDraggable(!isDraggable);
    }
  };

  const handleCancelEdit = useCallback(() => {
    setCurrentData(originalData);
    setIsDraggable(false);
    onReorder?.(originalData);
  }, [originalData, onReorder]);

  const handlePinToggle = useCallback(
    (item: T) => {
      if (pinColumn && pinColumn.onPinToggle) {
        pinColumn.onPinToggle(item);
      }
    },
    [pinColumn]
  );

  useEffect(() => {
    setCurrentData(data);
    setOriginalData(data);
  }, [data]);

  const tableContainerProps = {
    overflowX: "auto" as const,
    ref: tableContainerRef,
    className: "draggable-table-container",
    css: {
      "&::-webkit-scrollbar": {
        height: "8px",
      },
      "&::-webkit-scrollbar-track": {
        background: "rgba(0, 0, 0, 0.05)",
      },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(0, 0, 0, 0.2)",
        borderRadius: "4px",
      },
    },
  };

  const dragColumnWidth = "110px";

  const canDelete = useCallback(
    (item: T) => {
      if (canDeleteRow) {
        return canDeleteRow(item);
      }
      return true;
    },
    [canDeleteRow]
  );

  return (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sortableItems}
          strategy={verticalListSortingStrategy}
        >
          <Box {...tableContainerProps}>
            <Table variant="simple" style={tableStyle}>
              <Thead>
                <Tr>
                  <Th
                    width={dragColumnWidth}
                    style={{
                      width: dragColumnWidth,
                      minWidth: dragColumnWidth,
                      maxWidth: dragColumnWidth,
                      flex: `0 0 ${dragColumnWidth}`,
                    }}
                  >
                    <Flex align="center" gap={2} justify="center" width="100%">
                      {canDrag && (
                        <IconButton
                          size="sm"
                          bg="transparent"
                          color="#71717A"
                          border="1px solid #71717A"
                          _hover={{
                            backgroundColor: "rgba(113, 113, 122, 0.1)",
                          }}
                          _active={{
                            backgroundColor: "rgba(113, 113, 122, 0.2)",
                          }}
                          aria-label="cancel-edit"
                          onClick={handleCancelEdit}
                          icon={<X size={18} />}
                        />
                      )}
                      {!disableDragAndDrop && (
                        <IconButton
                          size="sm"
                          bg={canDrag ? "#286795 " : "transparent"}
                          color={canDrag ? "white" : "#286795"}
                          border={canDrag ? "" : "1px solid #286795"}
                          _hover={
                            canDrag
                              ? {
                                  backgroundColor: "rgba(40, 103, 149, 0.8)",
                                }
                              : {
                                  backgroundColor: "rgba(40, 103, 149, 0.1)",
                                }
                          }
                          _active={
                            canDrag
                              ? {
                                  backgroundColor: "rgba(40, 103, 149, 0.8)",
                                }
                              : {
                                  backgroundColor: "rgba(40, 103, 149, 0.1)",
                                }
                          }
                          aria-label="toggle-drag"
                          onClick={toggleDragMode}
                          icon={
                            canDrag ? (
                              <Check size={18} />
                            ) : (
                              <ArrowDownUp size={18} />
                            )
                          }
                        />
                      )}
                    </Flex>
                  </Th>
                  {columns.map((col) => (
                    <Th
                      key={col.key}
                      width={col.width}
                      textTransform="none"
                      fontSize="14px"
                    >
                      {col.header}
                    </Th>
                  ))}
                  {(showEditButton || showDeleteButton) && (
                    <Th
                      width={"120px"}
                      textAlign="center"
                      textTransform="none"
                      fontSize="14px"
                    >
                      {t("actions")}
                    </Th>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {isLoading ? (
                  <Tr>
                    <Td
                      colSpan={
                        columns.length +
                        (showEditButton || showDeleteButton ? 2 : 1)
                      }
                    >
                      <Center height="200px">
                        <Spinner
                          thickness="4px"
                          speed="0.65s"
                          emptyColor="gray.200"
                          color="blue.500"
                          size="xl"
                        />
                      </Center>
                    </Td>
                  </Tr>
                ) : (
                  currentData?.map((item) => (
                    <SortableRow
                      key={item.id}
                      item={item}
                      canDrag={canDrag}
                      rowStyle={rowStyle}
                      dragStyle={dragStyle}
                    >
                      <Td width={dragColumnWidth}>
                        <Flex justify="center" align="center" height="100%">
                          {canDrag ? (
                            <GripHorizontal size={16} opacity={0.5} />
                          ) : pinColumn ? (
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              cursor="pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePinToggle(item);
                              }}
                              _hover={{ opacity: 0.8 }}
                            >
                              {item.isFavorite ? (
                                <Pin size={16} color="#086AA9" fill="#086AA9" />
                              ) : (
                                <Pin size={16} color="#A1A1AA" />
                              )}
                            </Box>
                          ) : null}
                        </Flex>
                      </Td>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          column={col}
                          item={item}
                          onPinToggle={
                            col.showPin ? handlePinToggle : undefined
                          }
                        />
                      ))}
                      {(showEditButton || showDeleteButton) && (
                        <RowActions
                          item={item}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          showEdit={showEditButton}
                          showDelete={showDeleteButton}
                          disableDelete={!canDelete(item)}
                        />
                      )}
                    </SortableRow>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </SortableContext>
      </DndContext>

      {enablePagination && (
        <Flex justifyContent="flex-end" mt={4} mb={4} gap={2}>
          <Flex>
            <IconButton
              onClick={() => onPageChange?.(0)}
              isDisabled={currentPage === 0}
              transform={"rotate(180deg)"}
              color="black"
              icon={<DoubleArrowIcon />}
              mr={4}
              aria-label={t("firstPage")}
            />
            <IconButton
              onClick={() => onPageChange?.(currentPage - 1)}
              isDisabled={currentPage === 0}
              color="black"
              icon={<ChevronLeftIcon />}
              aria-label={t("previousPage")}
            />
          </Flex>

          <Flex alignItems="center" color="black">
            <Text mr={8}>
              {t("page")}{" "}
              <Text fontWeight="bold" as="span">
                {currentPage + 1}
              </Text>{" "}
              {t("of")}{" "}
              <Text fontWeight="bold" as="span">
                {Math.ceil(totalItems / pageSize) || 1}
              </Text>
            </Text>
          </Flex>

          <Flex>
            <IconButton
              onClick={() => onPageChange?.(currentPage + 1)}
              isDisabled={currentPage >= Math.ceil(totalItems / pageSize) - 1}
              color="black"
              icon={<ChevronRightIcon />}
              aria-label={t("nextPage")}
            />
            <IconButton
              onClick={() =>
                onPageChange?.(Math.ceil(totalItems / pageSize) - 1)
              }
              isDisabled={currentPage >= Math.ceil(totalItems / pageSize) - 1}
              color="black"
              icon={<DoubleArrowIcon />}
              ml={4}
              aria-label={t("lastPage")}
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
};

export default observer(DraggableTable);
